import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import {
    Box, Typography, Card, Stack, Chip, Button as MuiButton,
    Divider, IconButton, Snackbar, Alert, CircularProgress,
    Grid
} from '@mui/material';
import {
    ArrowLeft, Copy, Check, Hash, Image as ImageIcon,
    GalleryHorizontal, Zap, FileText, Sparkles, Calendar
} from 'lucide-react';

export default function PostDetail() {
    const { id } = useParams<{ id: string }>();
    const { posts } = useAppContext();
    const navigate = useNavigate();
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState(false);

    const post = posts.find(p => p.id === id);

    const handleCopy = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setSnackbar(true);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const CopyBtn = ({ text, field }: { text: string; field: string }) => (
        <IconButton
            size="small"
            onClick={() => handleCopy(text, field)}
            sx={{ color: copiedField === field ? '#10b981' : '#94a3b8', '&:hover': { color: '#4f46e5' } }}
        >
            {copiedField === field ? <Check size={15} /> : <Copy size={15} />}
        </IconButton>
    );

    if (!post) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2 }}>
                <FileText size={48} color="#94a3b8" />
                <Typography variant="h6" fontWeight={700} color="#111827">Post not found</Typography>
                <Typography variant="body2" color="#64748b">This post may have been deleted or doesn't exist.</Typography>
                <MuiButton
                    variant="outlined"
                    startIcon={<ArrowLeft size={16} />}
                    onClick={() => navigate('/dashboard')}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, mt: 1 }}
                >
                    Back to Dashboard
                </MuiButton>
            </Box>
        );
    }

    const typeIcon = post.postType === 'Carousel Post'
        ? <GalleryHorizontal size={18} color="#4f46e5" />
        : post.postType === 'Caption Post'
            ? <ImageIcon size={18} color="#4f46e5" />
            : <Zap size={18} color="#4f46e5" />;

    const formattedDate = new Date(post.createdAt).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
    });

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, borderRadius: 2 }}
                >
                    <ArrowLeft size={18} color="#334155" />
                </IconButton>
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" fontWeight={800} color="#111827" letterSpacing="-0.02em">
                        {post.topic}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
                        <Chip
                            icon={typeIcon}
                            label={post.postType}
                            size="small"
                            sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 700, fontSize: '0.72rem' }}
                        />
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Calendar size={13} color="#94a3b8" />
                            <Typography variant="caption" color="#94a3b8" fontWeight={600}>{formattedDate}</Typography>
                        </Stack>
                        <Chip label={post.tone} size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600, fontSize: '0.72rem' }} />
                        <Chip label={post.audience} size="small" sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 600, fontSize: '0.72rem' }} />
                    </Stack>
                </Box>
                <MuiButton
                    variant="contained"
                    startIcon={<Sparkles size={16} />}
                    onClick={() => navigate('/dashboard/generate')}
                    sx={{ bgcolor: '#4f46e5', textTransform: 'none', fontWeight: 700, borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: '#4338ca' } }}
                >
                    Generate New
                </MuiButton>
            </Box>

            <Grid container spacing={3}>
                {/* Left column — content */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Stack spacing={3}>
                        {/* Hook */}
                        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="caption" fontWeight={800} color="#94a3b8" letterSpacing="0.06em">HOOK</Typography>
                                <CopyBtn text={post.hook} field="hook" />
                            </Box>
                            <Typography variant="body1" fontWeight={600} color="#111827" lineHeight={1.7}>
                                {post.hook}
                            </Typography>
                        </Card>

                        {/* Caption */}
                        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="caption" fontWeight={800} color="#94a3b8" letterSpacing="0.06em">CAPTION</Typography>
                                <CopyBtn text={post.caption} field="caption" />
                            </Box>
                            <Typography variant="body2" color="#334155" lineHeight={1.8} sx={{ whiteSpace: 'pre-wrap' }}>
                                {post.caption}
                            </Typography>
                        </Card>

                        {/* Carousel Slides */}
                        {post.postType === 'Carousel Post' && post.carouselSlides && post.carouselSlides.length > 0 && (
                            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="caption" fontWeight={800} color="#94a3b8" letterSpacing="0.06em">CAROUSEL SLIDES</Typography>
                                    <CopyBtn text={post.carouselSlides.join('\n')} field="carousel" />
                                </Box>
                                <Stack spacing={1.5}>
                                    {post.carouselSlides.map((slide, i) => (
                                        <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                            <Box sx={{
                                                minWidth: 28, height: 28, borderRadius: 1.5, bgcolor: '#e0e7ff',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 0.2
                                            }}>
                                                <Typography variant="caption" fontWeight={800} color="#4f46e5">{i + 1}</Typography>
                                            </Box>
                                            <Typography variant="body2" color="#334155" lineHeight={1.7}>{slide}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Card>
                        )}
                    </Stack>
                </Grid>

                {/* Right column — hashtags & image prompt */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <Stack spacing={3}>
                        {/* Hashtags */}
                        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Hash size={16} color="#4f46e5" />
                                    <Typography variant="caption" fontWeight={800} color="#94a3b8" letterSpacing="0.06em">HASHTAGS</Typography>
                                </Stack>
                                <CopyBtn text={post.hashtags} field="hashtags" />
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {post.hashtags.split(' ').filter(Boolean).map((tag, i) => (
                                    <Chip
                                        key={i}
                                        label={tag}
                                        size="small"
                                        onClick={() => handleCopy(tag, `tag-${i}`)}
                                        sx={{
                                            bgcolor: '#f1f5f9', color: '#4f46e5', fontWeight: 600,
                                            fontSize: '0.72rem', cursor: 'pointer',
                                            '&:hover': { bgcolor: '#e0e7ff' }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Card>

                        {/* Image Prompt */}
                        {post.imagePrompt && (
                            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <ImageIcon size={16} color="#4f46e5" />
                                        <Typography variant="caption" fontWeight={800} color="#94a3b8" letterSpacing="0.06em">IMAGE PROMPT</Typography>
                                    </Stack>
                                    <CopyBtn text={post.imagePrompt} field="imagePrompt" />
                                </Box>
                                <Typography variant="body2" color="#64748b" lineHeight={1.7} sx={{ fontStyle: 'italic' }}>
                                    {post.imagePrompt}
                                </Typography>
                            </Card>
                        )}

                        {/* Quick Actions */}
                        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
                            <Typography variant="caption" fontWeight={800} color="#94a3b8" letterSpacing="0.06em" display="block" mb={2}>
                                QUICK ACTIONS
                            </Typography>
                            <Stack spacing={1.5}>
                                <MuiButton
                                    fullWidth variant="outlined"
                                    startIcon={<Copy size={15} />}
                                    onClick={() => handleCopy(`${post.hook}\n\n${post.caption}\n\n${post.hashtags}`, 'all')}
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#e2e8f0', color: '#334155', '&:hover': { bgcolor: '#f8fafc' } }}
                                >
                                    {copiedField === 'all' ? 'Copied!' : 'Copy Full Post'}
                                </MuiButton>
                                <MuiButton
                                    fullWidth variant="contained"
                                    startIcon={<Sparkles size={15} />}
                                    onClick={() => navigate('/dashboard/generate')}
                                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#4f46e5', boxShadow: 'none', '&:hover': { bgcolor: '#4338ca' } }}
                                >
                                    Generate Similar
                                </MuiButton>
                            </Stack>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar}
                autoHideDuration={2000}
                onClose={() => setSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" sx={{ borderRadius: 2, fontWeight: 600 }}>Copied to clipboard!</Alert>
            </Snackbar>
        </Box>
    );
}
